import React, { useState, useContext, useRef } from "react";
import { Button, Box, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../../connections/auth/AuthProvider";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import PropTypes from "prop-types";

import BoardVisualView from "./BoardVisualView.js";
import TablePreviewView from "./TablePreviewView.js";

const overviewTabStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  containerBox: {
    padding: theme.spacing(0),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    minHeight: "0",
    flex: "1 1 auto",
    overflow: "hidden",
  },
  card: {
    flex: 1,
    minWidth: "400px",
    height: "100%",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(0),
    overflow: "hidden",
    position: "relative",
  },
  controlCard: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: theme.spacing(0),
  },
  controlCardBody: {
    padding: 0,
  },
  controlCardContent: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  rightSection: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    flexShrink: 0,
    paddingTop: theme.spacing(1.5),
  },
  topButtons: {
    position: "relative",
    display: "inline-block",
    verticalAlign: "middle",
    marginRight: theme.spacing(2),
    flex: "0 0 auto",
  },
  viewButton: {
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  firstButton: {
    fontSize: "0.7rem",
    borderTopRightRadius: "0",
    borderBottomRightRadius: "0",
    borderColor: clientColor,
    color: clientColor,
    margin: "0",
    position: "relative",
    float: "left",
    "&:hover": {
      zIndex: "2",
    },
  },
  middleButton: {
    fontSize: "0.7rem",
    borderRadius: "0",
    borderColor: clientColor,
    color: clientColor,
    margin: "0",
    "&:hover": {
      zIndex: "2",
    },
  },
  lastButton: {
    fontSize: "0.7rem",
    borderTopLeftRadius: "0",
    borderBottomLeftRadius: "0",
    borderColor: clientColor,
    color: clientColor,
    margin: "0",
    "&:hover": {
      zIndex: "2",
    },
  },
  buttonSelected: {
    color: "white",
    backgroundColor: clientColor,
    "&:hover": {
      backgroundColor: clientColor,
    },
  },
});

BoardTab.propTypes = {
  scenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  externalSelectedScenarioId: PropTypes.number,
  onExternalScenarioHandled: PropTypes.func,
};

BoardTab.displayName = "BoardTab";

export default function BoardTab({
  scenarios,
  folders = [],
  externalSelectedScenarioId,
  onExternalScenarioHandled,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;

  const useStyles = makeStyles((theme) => overviewTabStyle(theme, clientColor));
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Track which sub-view is active
  const [currentView, setCurrentView] = useState("overview");

  // State management refs for each sub-view - passed to children
  const overviewStateRef = useRef({
    selectedScenario: "",
    selectedFilters: {},
    selectedVolume: "volume_sellthrough",
    selectedRoutes: "",
    mapView: "map",
  });

  const tablePreviewStateRef = useRef({
    selectedScenario: "",
    selectedFilters: {},
  });

  // Render view buttons component that each sub-view can use
  const renderViewButtons = () => (
    <div className={classes.topButtons}>
      <Button
        size="small"
        color="client"
        variant="outlined"
        simple={currentView !== "overview"}
        disabled={scenarios.length === 0}
        className={`${classes.viewButton} ${classes.firstButton} ${
          currentView === "overview" ? classes.buttonSelected : ""
        }`}
        onClick={() => setCurrentView("overview")}
      >
        {t("overview.overview_button")}
      </Button>
      <Button
        size="small"
        color="client"
        variant="outlined"
        simple={currentView !== "table_preview"}
        disabled={scenarios.length === 0}
        className={`${classes.viewButton} ${classes.lastButton} ${
          currentView === "table_preview" ? classes.buttonSelected : ""
        }`}
        onClick={() => setCurrentView("table_preview")}
      >
        {t("overview.table_preview_button")}
      </Button>
    </div>
  );

  return (
    <Box className={classes.containerBox}>
      {/* Content Area - Each sub-view includes controls + view buttons */}
      <Box style={{ position: "relative", flex: 1, overflow: "hidden" }}>
        {/* Overview Visual sub-view - includes own control bar */}
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transform:
              currentView === "overview"
                ? "translateX(0)"
                : "translateX(-200%)",
            transition: "none",
            pointerEvents: currentView === "overview" ? "auto" : "none",
          }}
        >
          <BoardVisualView
            scenarios={scenarios}
            folders={folders}
            stateRef={overviewStateRef}
            renderViewButtons={renderViewButtons}
            externalSelectedScenarioId={externalSelectedScenarioId}
            onExternalScenarioHandled={onExternalScenarioHandled}
          />
        </Box>

        {/* Table Preview sub-view - includes own control bar */}
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transform:
              currentView === "table_preview"
                ? "translateX(0)"
                : "translateX(-200%)",
            transition: "none",
            pointerEvents: currentView === "table_preview" ? "auto" : "none",
          }}
        >
          <TablePreviewView
            scenarios={scenarios}
            folders={folders}
            stateRef={tablePreviewStateRef}
            renderViewButtons={renderViewButtons}
          />
        </Box>
      </Box>
    </Box>
  );
}
